// tabsConfig.ts
import {
  HomeTab,
  BrokerViewTab,
  ClientHomeTab,
  LoggedOutTab,
  type BreakpointLayouts,
  type DashboardTab,
} from "./DashboardLayouts";
import { packLayouts } from "@/onboarding/templates/packLayout";

type TabConfig = {
  title: string;
  defaultWidgets: string[];
  layout: BreakpointLayouts;
};

export const getTabsConfig = (
  isInternalNewsquawkUser: boolean
): Record<string, TabConfig> => ({
  default: {
    title: "Home",
    defaultWidgets: isInternalNewsquawkUser
      ? HomeTab.widgets
      : ClientHomeTab.widgets,
    layout: isInternalNewsquawkUser ? HomeTab.layouts : ClientHomeTab.layouts,
  },
  // Conditionally add the BrokerView tab if the user is an internal Newsquawk user
  ...(isInternalNewsquawkUser && {
    BrokerView: {
      title: "Broker View",
      defaultWidgets: BrokerViewTab.widgets,
      layout: BrokerViewTab.layouts,
    },
  }),
});

function mapConfigToTabs(config: Record<string, TabConfig>): DashboardTab[] {
  return Object.entries(config).map(([tabId, entry]) => ({
    id: tabId,
    title: entry.title,
    layout: entry.layout,
    enabledWidgets: entry.defaultWidgets,
  }));
}

export function getDefaultTabs(
  isInternalNewsquawkUser: boolean
): DashboardTab[] {
  return mapConfigToTabs(getTabsConfig(isInternalNewsquawkUser));
}

const loggedOutTabsConfig = (): Record<string, TabConfig> => ({
  default: {
    title: "Newsquawk Demo",
    defaultWidgets: LoggedOutTab.widgets,
    // Packed against the real viewport rather than hand-authored: a fixed
    // h:10 grid leaves a dead band on any tall screen, and the guide rail
    // then floats in it, detached from the widget it is pointing at.
    layout: packLayouts(LoggedOutTab.widgets.map((id) => ({ id }))),
  },
  // No second tab. A visitor who has not yet worked out what the first
  // screen is does not benefit from a second one, and the guide replaces this
  // tab's contents with a desk built for whatever they say they trade.
  // LoggedOutExploreFeedsTab is kept in DashboardLayouts for reference.
});

export function getLoggedOutTabs(): DashboardTab[] {
  return mapConfigToTabs(loggedOutTabsConfig());
}
